// The field vocabulary of a table, shared by the storage shape (./data), the
// public shape (./public), the api's model layer and both apps.
import { properties } from "@repoflow/common/schema-fields";

const { createdAt, updatedAt } = properties;
export { createdAt, updatedAt };

export const orgId = { type: "string" };

// THE ONE FIELD THAT IS NOT LIKE THE OTHER SERVICES', AND IT CANNOT BE CHANGED
// LATER.
//
// Every other caller-chosen id in the platform is `/Name/Required` — lowercase
// kebab, `my-table`. That is wrong here for one reason: this id becomes a table
// name in SQL, and `SELECT * FROM my-table` parses as `my - table`, a
// subtraction over two identifiers. Measured on alasql 4.19.0.
//
// The archive worked around it by quoting everywhere — `CREATE TABLE
// \`${tableid}\`` in stack-pkg/src/libs/site-view/index.js — which does not stay
// in the engine: it pushes a backtick onto every query a tenant ever writes by
// hand, forever, including the four in tablews-docs/jobs/*/query.sql.
//
// So the id is a SQL identifier instead: a leading letter, then letters, digits
// and UNDERSCORES. 64 characters, which is MySQL's identifier limit and well
// under anything DynamoDB cares about.
//
// This is a one-way decision. `tableId` is the range key, so there is no rename
// — a "rename" is a new row and an orphaned old one — and rows already written
// under a kebab id could not be addressed by any query afterwards.
export const TABLE_ID_RE = /^[a-z][a-z0-9_]{0,63}$/;

// Reserved words are refused for the same reason and at the same moment. Not
// the whole SQL vocabulary — just the words a table name would collide with in
// the position a table name appears, which is what makes the message worth
// showing to a person ("`order` is a SQL keyword, try `orders`").
export const RESERVED_TABLE_IDS = [
  "select", "from", "where", "table", "order", "group", "by", "join", "on",
  "union", "insert", "update", "delete", "into", "values", "set", "index",
  "view", "user", "as", "and", "or", "not", "null", "distinct", "having",
  "limit", "offset", "left", "right", "inner", "outer", "with",
];

export const isValidTableId = (id) =>
  typeof id === "string" && TABLE_ID_RE.test(id) && !RESERVED_TABLE_IDS.includes(id);

export const tableId = {
  label: "Name",
  type: "string",
  pattern: TABLE_ID_RE.source,
  minLength: 1,
  maxLength: 64,
  default: "",
  description: "Lowercase letters, digits and underscores. This is the name you will use in SQL.",
};

export const title = { label: "Title", type: "string", minLength: 1 };

export const description = {
  label: "Description",
  type: "string",
  textarea: true,
  default: "",
};

export const tags = {
  label: "Tags",
  type: "array",
  items: { type: "string" },
  widget: "tags",
  default: [],
};

// The columns a `system` table declares, as /Types/Param — the same shape
// pagews' section params and wfws' capture-step fields use, so
// @repoflow/components/types-form declares them and types-values renders a row
// against the declaration, both unmodified.
//
// A SOURCED table leaves this empty and carries the columns the source gave it
// on the rows object instead. The two are not merged: a source's shape changes
// when somebody adds a field in Airtable, and a stored copy would go stale
// silently.
export const columns = {
  label: "Columns",
  type: "array",
  items: { $ref: "/Types/Param" },
  index: "name",
  default: [],
};

// The storage twin of `columns`, and the reason is the same one
// projects/wfws/common/schema/workflow/fields.js gives for storedSteps:
// /Types/Param carries a `config` oneOf resolved by the custom `resolver`
// keyword, datorm validates every write with raw `jsonschema`, and raw
// jsonschema has never heard of `resolver` — so it evaluates the union
// literally, matches several structurally-identical branches at once, and
// answers "is not exactly one from" for every real value. A storage shape
// carrying the union would refuse every row.
//
// The public shape keeps the real thing, and assertValidResolved
// (@repoflow/api-server/validate) does the resolver-aware pass at the API
// boundary, which is where a tenant's input is actually checked.
export const storedColumns = {
  type: "array",
  items: { type: "object" },
  default: [],
};

// WHERE A TABLE'S ROWS COME FROM.
//
// `system` is a table the tenant fills in themselves; the other three are pulled
// from somewhere else by the sync handler. Ported from the archive's
// stack-pkg/src/data/domain-table/fields, which had exactly these four.
//
// This is the RESOLVER property: `source` below is a oneOf whose branch is
// chosen by this field's position in the enum. @repoflow/schema resolves it and
// form-schema renders it, which is what makes one form show four different sets
// of fields.
//
// Named `sourceType` rather than `type`, deliberately. A table's columns are
// /Types/Param, every one of which has a `type` of its own, and two fields
// called `type` in one form is a bug waiting for somebody to write
// `values.type` and be right half the time.
export const sourceType = {
  label: "Source",
  type: "string",
  enum: ["system", "airtable", "gsheet", "csv"],
  default: "system",
};

// THE CREDENTIAL IS NOT IN HERE, and that is the point of this comment.
//
// The archive stored the Airtable API key in `source.config.key` — a plain
// string on the row, returned by the GraphQL query like any other field, masked
// only by `password: true` in the form. Anyone who could read a table could read
// the key that reads the whole base.
//
// Here `source` carries only the non-secret coordinates: which base, which
// table, which view. The credential is an attribute BESIDE this schema, written
// by its own mutation and read by the sync handler alone — see
// data/table.setCredential and the long note above it, which also says why it
// is a row attribute rather than the Secrets Manager entry originally planned.
export const source = {
  // NOT "Source", and not "Source details" either.
  //
  // `sourceType` above already carries "Source", and two fields with one label
  // in the same form are ambiguous on screen. "Source details" looks like it
  // fixes that and does not: the e2e suite's `labelled()` matches a <dt> whose
  // text STARTS with the label followed by a space — because tablews is the
  // first project with a field carrying a `description`, which form-schema
  // renders inside the <dt> — so "Source" would match this field too and every
  // locator for either becomes ambiguous. A label that does not share the
  // prefix is the only version that is unambiguous in both places.
  label: "Connection",
  type: "object",
  oneOf: [
    { $ref: "/Table/Source/System" },
    { $ref: "/Table/Source/Airtable" },
    { $ref: "/Table/Source/GoogleSheet" },
    { $ref: "/Table/Source/Csv" },
  ],
  resolver: "sourceType",
  default: {},
};

// The storage twin, for the reason storedColumns gives above: datorm validates
// with raw jsonschema, which cannot evaluate `resolver` and so refuses every
// value of a oneOf whose branches are structurally similar.
export const storedSource = { type: "object", default: {} };

// The four branches. Each is only the coordinates — nothing here is a secret.
export const sourceSchema = [
  {
    // A table the tenant fills in themselves. No properties at all, and
    // `maxProperties: 0` so that switching to it from a sourced type cannot
    // leave the previous branch's fields behind on the row.
    id: "/Table/Source/System",
    type: "object",
    properties: {},
    maxProperties: 0,
    default: {},
  },
  {
    id: "/Table/Source/Airtable",
    type: "object",
    required: ["base", "table"],
    properties: {
      base: { label: "Base ID", type: "string", minLength: 1, default: "" },
      table: { label: "Table name", type: "string", minLength: 1, default: "" },
      // Airtable's own default view. Empty means "the table's default", which
      // is what the API does with an absent `view`.
      view: { label: "View name", type: "string", default: "" },
    },
    default: {},
  },
  {
    id: "/Table/Source/GoogleSheet",
    type: "object",
    required: ["id", "sheet"],
    properties: {
      id: { label: "Spreadsheet ID", type: "string", minLength: 1, default: "" },
      sheet: { label: "Sheet name", type: "string", minLength: 1, default: "" },
    },
    default: {},
  },
  {
    id: "/Table/Source/Csv",
    type: "object",
    required: ["url", "separator"],
    properties: {
      // NO `widget: "url"`. pagews has one, but it is a component under its own
      // app/code/src/widgets and these are separate npm packages with no
      // dependency between them — and app/shared/widgets/register.js says what
      // naming a widget this app does not register costs: getField() resolves a
      // widget for EVERY property, so it is a runtime `widget.missing:url`
      // thrown from inside the render, which takes the whole Source tab with
      // it. Measured on br-w01: the field never appeared and two e2e cases timed
      // out waiting for it.
      //
      // The `pattern` is the same rule the handler enforces, said early. A
      // non-https URL is refused there — `file:` reads the Lambda's own disk —
      // and a person should learn that while typing rather than from a failed
      // sync a minute later.
      url: {
        label: "URL",
        description: "An https address the file can be fetched from.",
        type: "string",
        pattern: "^https://",
        minLength: 1,
        default: "",
      },
      separator: { label: "Separator", type: "string", minLength: 1, maxLength: 4, default: "," },
    },
    default: {},
  },
];

// What the last sync did. ONE nested object rather than five columns, so the
// handler writes one attribute and the screen polls one field.
//
// `status` here is the SYNC's, not the table's — a failed sync leaves the table
// `active` with its previous rows intact and only this saying what went wrong.
// The archive set `table.status = "error"`, which made the whole row look dead
// when all that had happened was that somebody rotated an API key.
export const sync = {
  label: "Sync",
  type: "object",
  properties: {
    status: { type: "string", default: "idle" },
    syncId: { type: "string", default: "" },
    startedAt: { type: "string", default: "" },
    finishedAt: { type: "string", default: "" },
    rows: { type: "integer", minimum: 0, default: 0 },
    error: { type: "string", default: "" },
  },
  default: {},
};

export const SYNC_IDLE = "idle";
export const SYNC_PROCESSING = "processing";
// The last sync finished and the rows in the bucket are what it fetched.
// Distinct from `idle`, which a table that has never synced also reads as —
// `finishedAt` tells them apart, but only if you know to look, and the screen
// should not have to.
export const SYNC_DONE = "done";
export const SYNC_ERROR = "error";

// The last four characters of the credential, so the form can say `pat…AbCd`
// and a person can tell whether the key they are looking at is the one they
// meant. Never the credential.
export const credentialHint = { type: "string", default: "" };

export const status = { type: "string" };

// How many rows the object in S3 currently holds, and its ETag. Both are
// mirrors of what S3 last said, kept on the row so a listing can show a count
// without reading every object.
//
// `rowsEtag` is a CACHE and never the authority. The conditional write is
// against the etag S3 returned in the same response as the rows — see
// data/table-rows. A crash between the S3 put and the DynamoDB update would
// otherwise leave this stale and make every later write conflict for ever.
export const rowCount = { type: "integer", minimum: 0, default: 0 };
export const rowsEtag = { type: "string", default: "" };

// `shareToken` is deliberately NOT here, and lands with the share link. It keys
// a sparse GSI, DynamoDB refuses null on an index key, and datorm serializes
// every schema property on every write with absent as null — so it has to live
// beside the schema rather than in it. `views-table` carries the same note.
